import Mention, { type MentionOptions } from "@tiptap/extension-mention"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { DoubleSpaceMention } from "./double-space-mention"
import { MentionChipView, type PromptMentionExtensionOptions } from "./mention-chip-view"
import { mentionDisplayLabel, mentionToken, type MentionCategory } from "./types"

/** Shared chip classes for TipTap mention node and read-only prompt previews. */
export const MENTION_CATEGORY_CLASS: Record<MentionCategory, string> = {
  hero: "pe-mention pe-mention--hero",
  loc: "pe-mention pe-mention--loc",
  prop: "pe-mention pe-mention--prop",
  shot: "pe-mention pe-mention--shot",
  reference: "pe-mention pe-mention--reference",
  cam: "pe-mention pe-mention--cam",
  attr: "pe-mention pe-mention--attr",
  file: "pe-mention pe-mention--file",
  person: "pe-mention pe-mention--person",
  task: "pe-mention pe-mention--task",
  script: "pe-mention pe-mention--script",
  node: "pe-mention pe-mention--node",
  img: "pe-mention pe-mention--img",
  vid: "pe-mention pe-mention--img",
}

type PromptMentionOptions = MentionOptions & PromptMentionExtensionOptions

/** Mention с категорией (герой/локация/реквизит/референс/камера) и стабильным текстовым токеном. */
export const PromptMention = Mention.extend<PromptMentionOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      getMentionItems: () => [],
      getOnMentionInsert: () => undefined,
    } as PromptMentionOptions
  },

  addExtensions() {
    const inherited = this.parent?.() ?? []
    // TaskMention копирует этот метод и зовёт parent(): без проверки триггер регистрируется дважды.
    if (inherited.some((extension) => extension.name === DoubleSpaceMention.name)) return inherited
    return [...inherited, DoubleSpaceMention]
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-id"),
        renderHTML: (attrs) => (attrs.id == null ? {} : { "data-id": String(attrs.id) }),
      },
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-label"),
        renderHTML: (attrs) => (attrs.label == null ? {} : { "data-label": String(attrs.label) }),
      },
      category: {
        default: "reference",
        parseHTML: (el) => el.getAttribute("data-category"),
        renderHTML: (attrs) => (attrs.category == null ? {} : { "data-category": String(attrs.category) }),
      },
      stateId: {
        default: null,
        parseHTML: (el) => {
          const raw = el.getAttribute("data-state-id")
          return raw == null || raw === "" ? null : Number(raw)
        },
        renderHTML: (attrs) =>
          attrs.stateId == null || attrs.stateId === "" ? {} : { "data-state-id": String(attrs.stateId) },
      },
      attrKey: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-attr-key"),
        renderHTML: (attrs) => (attrs.attrKey == null ? {} : { "data-attr-key": String(attrs.attrKey) }),
      },
    }
  },

  renderText({ node }) {
    const category = (node.attrs.category ?? "reference") as MentionCategory
    const stateIdRaw = node.attrs.stateId
    const stateId = stateIdRaw == null || stateIdRaw === "" ? null : Number(stateIdRaw)
    const attrKey = node.attrs.attrKey == null || node.attrs.attrKey === "" ? null : String(node.attrs.attrKey)
    return mentionToken(category, Number(node.attrs.id ?? 0), String(node.attrs.label ?? ""), stateId, attrKey)
  },

  renderHTML({ node, HTMLAttributes }) {
    const category = (node.attrs.category ?? "reference") as MentionCategory
    const display = mentionDisplayLabel(category, String(node.attrs.label ?? ""))
    return [
      "span",
      { ...HTMLAttributes, class: MENTION_CATEGORY_CLASS[category] ?? "pe-mention", "data-category": category },
      display,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionChipView)
  },
})
